import { clsx } from 'clsx';
import { SECONDS_IN_MINUTE } from '@seekingalpha/samw-utils';
import { useMemo } from 'react';

import { Tooltip } from 'UI/Components/Tooltip';
import { Wrap } from 'Shared/Wrap';

import { useIsAudioAvailable } from '../useIsAudioAvailable';
import { useIsAudioPlaying } from '../useIsAudioPlaying';
import { useHandlePlayClick } from '../useHandlePlayClick';
import { useButtonLockStatus } from '../useButtonLockStatus';

import PlaySvg from './play.svg';
import PauseSvg from './pause.svg';

import type { FullArticleT } from 'Shared/types/ApiModels';
import type { ArticleTypeT } from 'Constants/articleTypes';
import type { ServicePlanArticleT } from 'Services/api/types';

type PropsT = {
  readonly article: FullArticleT | ServicePlanArticleT;
  readonly articleType: ArticleTypeT;
  readonly buttonRef: React.Ref<HTMLButtonElement>;
  readonly hideLabel?: boolean;
  readonly isMpPaidSubscriber?: boolean;
  readonly mpServicePlanId?: string;
  readonly id?: string;
};

const PREMIUM_TOOLTIP = 'Listening to articles is a Premium feature';
const PRO_TOOLTIP = 'Listening to this article is a PRO feature';
const MP_TOOLTIP = 'Listening to this article is a service subscriber feature';

export const PlayButton = ({
  article,
  articleType,
  hideLabel,
  buttonRef,
  isMpPaidSubscriber,
  mpServicePlanId,
  id,
}: PropsT) => {
  const isPlaying = useIsAudioPlaying(article.id, articleType, mpServicePlanId);
  const { isLocked, isProLocked } = useButtonLockStatus({
    isProContent: article.isLockedPro,
    isMpPaidSubscriber,
    articleType,
  });
  const isAudioAvailable = useIsAudioAvailable(
    article.audioDuration,
    articleType,
  );
  const Icon = isPlaying ? PauseSvg : PlaySvg;
  const duration = (article.audioDuration ?? 0) / SECONDS_IN_MINUTE;
  const lockedStyles = isLocked ? 'text-black-20' : 'text-share-link';

  const handleClick = useHandlePlayClick({
    article,
    articleType,
    isLocked,
    isProLocked,
    isPlaying,
    mpServicePlanId,
  });

  const tooltip = useMemo(() => {
    if (isLocked && mpServicePlanId) {
      return MP_TOOLTIP;
    }

    return isProLocked ? PRO_TOOLTIP : PREMIUM_TOOLTIP;
  }, [isLocked, mpServicePlanId, isProLocked]);

  if (!isAudioAvailable) {
    return null;
  }

  return (
    <div className="flex items-center text-medium-r">
      <Wrap with={isLocked ? <Tooltip message={tooltip} /> : null}>
        <button
          aria-label={isPlaying ? 'Pause' : 'Play'}
          aria-pressed={isPlaying}
          className={clsx(isProLocked && 'cursor-default')}
          id={id}
          ref={buttonRef}
          type="button"
          onClick={handleClick}
        >
          <span className="flex items-center">
            <Icon className={lockedStyles} />

            {hideLabel ? null : (
              <span className={clsx('ml-8 text-medium-b', lockedStyles)}>
                {isPlaying ? 'Pause' : 'Play'}
              </span>
            )}
          </span>
        </button>
      </Wrap>

      <span className="ml-4 text-medium-r text-share-text-2">
        ({`${duration < 1 ? '<1' : Math.round(duration)}min`})
      </span>
    </div>
  );
};
